import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getHostel, getTrip } from "../partners/madmonkey/content";
import HostelPage from "../partners/madmonkey/HostelPage";
import MadMonkeyHome from "../partners/madmonkey/MadMonkeyHome";
import MadMonkeyLayout from "../partners/madmonkey/MadMonkeyLayout";
import { HOME_HEAD, hostelHead, routeHead } from "../partners/madmonkey/routeHead";
import RoutePage from "../partners/madmonkey/RoutePage";
import { scrollToId } from "../partners/madmonkey/scroll";
import { isEmbed } from "../partners/shared/embed";
import NotFound from "./NotFound";

interface MadMonkeyPageProps {
	view: "home" | "route" | "hostel";
	match: { params: { routeId?: string; hostelSlug?: string } };
	/** a hostel page's synced list, read by the server (loader `madMonkeyHostel`); the other two views have no loader */
	initialData?: unknown;
}

// The three routes each mount this page afresh, so "has the page been shown yet" lives here and not in a ref.
let shownOnce = false;

/**
 * `/mad-monkey`, `/mad-monkey/routes/:routeId` and `/mad-monkey/hostels/:hostelSlug`: the Stippl x Mad Monkey
 * partner page, a confidential demo. English only, noindex, in no sitemap and linked from nowhere on the site.
 * One page component for the three, so the visitor's route builder state and the pages' look carry across them
 * as client-side navigation. An id or slug that names nothing renders the site's own not-found page (the
 * server answers it with a 404, see `knownParams` in marketingRoutes.tsx).
 */
const MadMonkeyPage = ({ view, match, initialData }: MadMonkeyPageProps) => {
	const location = useLocation();
	const routeId = match.params.routeId ?? "";
	const hostelSlug = match.params.hostelSlug ?? "";
	const trip = view === "route" ? getTrip(routeId) : undefined;
	const hostel = view === "hostel" ? getHostel(hostelSlug) : undefined;
	const notFound = (view === "route" && !trip) || (view === "hostel" && !hostel);
	// Only a hostel page is framed by Mad Monkey's site (graphql/helpers/partnerFraming.ts); the other two are always whole pages.
	const embed = view === "hostel" && isEmbed(location.search);

	// Moving between the three pages is a client-side navigation, so the tab title follows by hand.
	const title = (view === "route" ? routeHead(routeId) : view === "hostel" ? hostelHead(hostelSlug) : HOME_HEAD)?.title;
	useEffect(() => {
		if (title) document.title = title;
	}, [title]);

	// A link to a section scrolls to it; any other arrival starts at the top (the first load keeps the browser's place).
	useEffect(() => {
		const id = location.hash.slice(1);
		if (id) requestAnimationFrame(() => scrollToId(id));
		else if (shownOnce) window.scrollTo(0, 0);
		shownOnce = true;
	}, [location.pathname, location.hash, location.key]);

	if (notFound) return <NotFound />;

	return (
		<MadMonkeyLayout embed={embed}>
			{trip ? <RoutePage trip={trip} /> : hostel ? <HostelPage slug={hostelSlug} initialData={initialData} embed={embed} /> : <MadMonkeyHome />}
		</MadMonkeyLayout>
	);
};

export default MadMonkeyPage;
